"use client";

import { useEffect } from "react";
import Link from "next/link";

interface ErrorBoundaryProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function ErrorBoundary({ error, reset }: ErrorBoundaryProps) {
  useEffect(() => {
    // Log error to console
    console.error("Error boundary caught error:", error);
    
    // Send to PostHog using captureException as per official docs
    // https://posthog.com/docs/error-tracking/installation/nextjs
    if (typeof window !== 'undefined' && window.posthog && window.posthog.__loaded) {
      if (window.posthog.captureException) {
        window.posthog.captureException(error, {
          source: "error_boundary",
          route: window.location.pathname,
        });
      }
    }
  }, [error]);

  // Extract request ID if present in error message
  const requestIDMatch = error.message?.match(/request[_-]?id[:\s]+([a-f0-9-]+)/i);
  const requestID = requestIDMatch ? requestIDMatch[1] : error.digest;

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: "#F8F5E9" }}>
      <div className="max-w-md w-full space-y-6 text-center">
        <div>
          <h1 className="text-3xl font-semibold mb-2" style={{ color: "#0A3740" }}>
            Something went wrong
          </h1>
          <p className="text-base" style={{ color: "#243F42" }}>
            We&apos;ve logged the error and are looking into it. Please try refreshing the page or go back to the home page.
          </p>
        </div>

        {requestID && (
          <div className="rounded-lg border px-4 py-3 text-sm text-left" style={{ borderColor: "rgba(10, 55, 64, 0.2)", backgroundColor: "#ffffff" }}>
            <p className="font-medium mb-1" style={{ color: "#0A3740" }}>
              Reference ID:
            </p>
            <p className="font-mono text-xs break-all" style={{ color: "#6b7280" }}>
              {requestID}
            </p>
            <p className="text-xs mt-2" style={{ color: "#6b7280" }}>
              If this keeps happening, please contact support and include this reference ID.
            </p>
          </div>
        )}

        <div className="flex gap-3 justify-center">
          <button
            onClick={reset}
            className="px-5 py-2.5 rounded-lg font-semibold transition"
            style={{
              backgroundColor: "#D4AF37",
              color: "#0A3740",
            }}
          >
            Try again
          </button>
          <Link
            href="/"
            className="px-5 py-2.5 rounded-lg font-semibold transition border"
            style={{
              borderColor: "rgba(10, 55, 64, 0.2)",
              color: "#0A3740",
            }}
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

